import React, { useState, useEffect } from 'react';
import { useTasks } from '../../hooks/useTasks';

export default function TaskSettings({ onClose }) {
  const { fetchSettings, updateSettings } = useTasks();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadSettings = async () => {
      const data = await fetchSettings();
      setSettings(data || getDefaultSettings());
      setLoading(false);
    };

    loadSettings();
  }, []);

  const getDefaultSettings = () => ({
    smartTasksEnabled: true,
    dailyEngagement: {
      cashFlowSummary: { enabled: true },
      healthCheck: { enabled: true },
      todaysFocus: { enabled: true },
      quickWins: { enabled: true, count: 3 },
      upcomingWeek: { enabled: true },
      streaksAchievements: { enabled: true },
      smartSuggestions: { enabled: true }
    },
    eventTriggered: {
      overduePayments: { enabled: true },
      unapprovedInvoices: { enabled: true, daysAfter: 3 },
      invoicesRequiringApproval: { enabled: true },
      upcomingPayments: { enabled: true, daysBefore: 2 },
      largeInvoices: { enabled: true, threshold: 2000, daysBefore: 3 },
      recurringPattern: { enabled: false },
      missingPaymentMethod: { enabled: false, daysBefore: 7 },
      thankCustomers: { enabled: false },
      unusualActivity: { enabled: false }
    },
    display: {
      showCompleted: true,
      autoArchiveAfterDays: 7,
      showBadgeCount: true
    }
  });

  const handleSave = async () => {
    setSaving(true);
    const result = await updateSettings(settings);
    
    if (result.success) {
      alert('Settings saved successfully!');
      onClose();
    } else {
      alert('Failed to save settings: ' + (result.error || 'Unknown error'));
    }
    
    setSaving(false);
  };

  const updateSetting = (path, value) => {
    const newSettings = { ...settings };
    const keys = path.split('.');
    let current = newSettings;
    
    for (let i = 0; i < keys.length - 1; i++) {
      current = current[keys[i]];
    }
    
    current[keys[keys.length - 1]] = value;
    setSettings(newSettings);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">⚙️ Smart Task Settings</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-8">
          {/* Master Toggle */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  🤖 Smart Task Generation
                </h3>
                <p className="text-gray-700">
                  Let Receevit® automatically create tasks for you based on your invoice activity.
                  You can still add your own tasks anytime.
                </p>
              </div>
              <div className="ml-6">
                <button
                  type="button"
                  onClick={() => updateSetting('smartTasksEnabled', !settings.smartTasksEnabled)}
                  className={`relative inline-flex h-8 w-16 items-center rounded-full transition-colors ${
                    settings.smartTasksEnabled ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                      settings.smartTasksEnabled ? 'translate-x-9' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>

          {/* Daily Engagement Tasks */}
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              🌅 Daily Engagement Tasks
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              These tasks appear every day to keep you informed and engaged
            </p>
            
            <div className="space-y-4">
              <SettingToggle
                label="💰 Daily Cash Flow Summary"
                description="Morning briefing of incoming/outgoing payments"
                enabled={settings.dailyEngagement?.cashFlowSummary?.enabled !== false}
                onChange={(val) => updateSetting('dailyEngagement.cashFlowSummary.enabled', val)}
                disabled={!settings.smartTasksEnabled}
              />
              
              <SettingToggle
                label="📊 Daily Invoice Health Check"
                description="Overview of invoice statuses and issues"
                enabled={settings.dailyEngagement?.healthCheck?.enabled !== false}
                onChange={(val) => updateSetting('dailyEngagement.healthCheck.enabled', val)}
                disabled={!settings.smartTasksEnabled}
              />
              
              <SettingToggle
                label="🎯 Today's Focus (AI Prioritization)"
                description="AI selects your most important task for the day"
                enabled={settings.dailyEngagement?.todaysFocus?.enabled !== false}
                onChange={(val) => updateSetting('dailyEngagement.todaysFocus.enabled', val)}
                disabled={!settings.smartTasksEnabled}
              />
              
              <SettingToggle
                label="⚡ Quick Wins (Gamification)"
                description="3 easy tasks to build momentum"
                enabled={settings.dailyEngagement?.quickWins?.enabled !== false}
                onChange={(val) => updateSetting('dailyEngagement.quickWins.enabled', val)}
                disabled={!settings.smartTasksEnabled}
              />
              
              <SettingToggle
                label="📅 Upcoming This Week"
                description="Week-ahead view of payments and deadlines"
                enabled={settings.dailyEngagement?.upcomingWeek?.enabled !== false}
                onChange={(val) => updateSetting('dailyEngagement.upcomingWeek.enabled', val)}
                disabled={!settings.smartTasksEnabled}
              />
              
              <SettingToggle
                label="🔥 Streaks & Achievements"
                description="Track your productivity streaks and unlock badges"
                enabled={settings.dailyEngagement?.streaksAchievements?.enabled !== false}
                onChange={(val) => updateSetting('dailyEngagement.streaksAchievements.enabled', val)}
                disabled={!settings.smartTasksEnabled}
              />
            </div>
          </div>

          {/* Event-Triggered Tasks - Sent Invoices */}
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              📤 Sent Invoices (You're waiting for payment)
            </h3>
            
            <div className="space-y-4">
              <SettingToggle
                label="🔴 Overdue Payments"
                description="Alert immediately when payment becomes overdue"
                enabled={settings.eventTriggered?.overduePayments?.enabled !== false}
                onChange={(val) => updateSetting('eventTriggered.overduePayments.enabled', val)}
                disabled={!settings.smartTasksEnabled}
              />
              
              <SettingWithInput
                label="🟡 Unapproved Guest Invoices"
                description="Alert when invoice not approved after X days"
                enabled={settings.eventTriggered?.unapprovedInvoices?.enabled !== false}
                onChange={(val) => updateSetting('eventTriggered.unapprovedInvoices.enabled', val)}
                inputLabel="Alert after (days):"
                inputValue={settings.eventTriggered?.unapprovedInvoices?.daysAfter || 3}
                onInputChange={(val) => updateSetting('eventTriggered.unapprovedInvoices.daysAfter', parseInt(val))}
                disabled={!settings.smartTasksEnabled}
              />
              
              <SettingWithInput
                label="🔵 Large Invoices Due Soon"
                description="Alert for high-value invoices approaching due date"
                enabled={settings.eventTriggered?.largeInvoices?.enabled !== false}
                onChange={(val) => updateSetting('eventTriggered.largeInvoices.enabled', val)}
                inputLabel="For invoices ≥ £"
                inputValue={settings.eventTriggered?.largeInvoices?.threshold || 2000}
                onInputChange={(val) => updateSetting('eventTriggered.largeInvoices.threshold', parseInt(val))}
                secondInputLabel="Alert (days before):"
                secondInputValue={settings.eventTriggered?.largeInvoices?.daysBefore || 3}
                onSecondInputChange={(val) => updateSetting('eventTriggered.largeInvoices.daysBefore', parseInt(val))}
                disabled={!settings.smartTasksEnabled}
              />
            </div>
          </div>

          {/* Event-Triggered Tasks - Received Invoices */}
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              📥 Received Invoices (You need to pay)
            </h3>
            
            <div className="space-y-4">
              <SettingToggle
                label="🟡 Invoices Requiring Your Approval"
                description="Alert immediately when invoice received"
                enabled={settings.eventTriggered?.invoicesRequiringApproval?.enabled !== false}
                onChange={(val) => updateSetting('eventTriggered.invoicesRequiringApproval.enabled', val)}
                disabled={!settings.smartTasksEnabled}
              />
              
              <SettingWithInput
                label="🔵 Upcoming Payments You Need to Make"
                description="Remind before automatic payment is taken"
                enabled={settings.eventTriggered?.upcomingPayments?.enabled !== false}
                onChange={(val) => updateSetting('eventTriggered.upcomingPayments.enabled', val)}
                inputLabel="Remind me (days before):"
                inputValue={settings.eventTriggered?.upcomingPayments?.daysBefore || 2}
                onInputChange={(val) => updateSetting('eventTriggered.upcomingPayments.daysBefore', parseInt(val))}
                disabled={!settings.smartTasksEnabled}
              />
            </div>
          </div>

          {/* Optional Tasks */}
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              💡 Optional Suggestions (Advanced)
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              These are nice-to-have features. Disable if they feel intrusive.
            </p>
            
            <div className="space-y-4">
              <SettingToggle
                label="🔵 Recurring Invoice Patterns"
                description="Suggest when to send recurring invoices"
                enabled={settings.eventTriggered?.recurringPattern?.enabled === true}
                onChange={(val) => updateSetting('eventTriggered.recurringPattern.enabled', val)}
                disabled={!settings.smartTasksEnabled}
              />
              
              <SettingToggle
                label="🟢 Thank Customers After Payment"
                description="Create task to thank customer 1 day after payment"
                enabled={settings.eventTriggered?.thankCustomers?.enabled === true}
                onChange={(val) => updateSetting('eventTriggered.thankCustomers.enabled', val)}
                disabled={!settings.smartTasksEnabled}
              />
              
              <SettingToggle
                label="⚠️ Unusual Activity Alerts"
                description="Warn when customer behavior deviates from normal"
                enabled={settings.eventTriggered?.unusualActivity?.enabled === true}
                onChange={(val) => updateSetting('eventTriggered.unusualActivity.enabled', val)}
                disabled={!settings.smartTasksEnabled}
              />
            </div>
          </div>

          {/* Display Options */}
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              📊 Display Options
            </h3>
            
            <div className="space-y-4">
              <SettingToggle
                label="Show Completed Tasks in Main View"
                description="Display recent completed tasks for satisfaction"
                enabled={settings.display?.showCompleted !== false}
                onChange={(val) => updateSetting('display.showCompleted', val)}
              />
              
              <SettingWithInput
                label="Auto-archive Completed Tasks"
                description="Move old completed tasks to archive"
                enabled={settings.display?.autoArchiveAfterDays !== null}
                onChange={(val) => updateSetting('display.autoArchiveAfterDays', val ? 7 : null)}
                inputLabel="After (days):"
                inputValue={settings.display?.autoArchiveAfterDays || 7}
                onInputChange={(val) => updateSetting('display.autoArchiveAfterDays', parseInt(val))}
              />
              
              <SettingToggle
                label="Show Badge Count on To-Do Tab"
                description="Display notification badge with task count on the To-Do navigation tab"
                enabled={settings.display?.showBadgeCount !== false}
                onChange={(val) => updateSetting('display.showBadgeCount', val)}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-between items-center pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => {
                if (confirm('Reset all settings to defaults?')) {
                  setSettings(getDefaultSettings());
                }
              }}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
            >
              Reset to Defaults
            </button>
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper component for toggle settings
function SettingToggle({ label, description, enabled, onChange, disabled = false }) {
  return (
    <div className={`flex items-start justify-between p-4 bg-white border border-gray-200 rounded-lg ${disabled ? 'opacity-50' : ''}`}>
      <div className="flex-1">
        <h4 className="font-medium text-gray-900 mb-1">{label}</h4>
        <p className="text-sm text-gray-600">{description}</p>
      </div>
      <button
        type="button"
        onClick={() => !disabled && onChange(!enabled)}
        disabled={disabled}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          enabled && !disabled ? 'bg-blue-600' : 'bg-gray-300'
        } ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            enabled ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );
}

// Helper component for settings with input
function SettingWithInput({ 
  label, description, enabled, onChange, 
  inputLabel, inputValue, onInputChange,
  secondInputLabel, secondInputValue, onSecondInputChange,
  disabled = false 
}) {
  return (
    <div className={`p-4 bg-white border border-gray-200 rounded-lg ${disabled ? 'opacity-50' : ''}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h4 className="font-medium text-gray-900 mb-1">{label}</h4>
          <p className="text-sm text-gray-600">{description}</p>
        </div>
        <button
          type="button"
          onClick={() => !disabled && onChange(!enabled)}
          disabled={disabled}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            enabled && !disabled ? 'bg-blue-600' : 'bg-gray-300'
          } ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              enabled ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>
      
      {enabled && !disabled && (
        <div className="flex items-center space-x-4 mt-3 pl-4">
          <label className="text-sm text-gray-700">{inputLabel}</label>
          <input
            type="number"
            value={inputValue}
            onChange={(e) => onInputChange(e.target.value)}
            className="w-20 px-3 py-1 border border-gray-300 rounded-lg text-sm"
            min="1"
          />
          
          {secondInputLabel && (
            <>
              <label className="text-sm text-gray-700">{secondInputLabel}</label>
              <input
                type="number"
                value={secondInputValue}
                onChange={(e) => onSecondInputChange(e.target.value)}
                className="w-20 px-3 py-1 border border-gray-300 rounded-lg text-sm"
                min="1"
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}

